import { Link, createFileRoute } from '@tanstack/react-router'
import { useAuthActions } from '@convex-dev/auth/react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { convexQuery } from '@convex-dev/react-query'
import { api } from '../../convex/_generated/api'
import * as React from 'react'

export const Route = createFileRoute('/')({
  component: Home,
})

const HERO_PHRASES = [
  'research teams',
  'investigative journalists',
  'fact-checkers',
  'policy analysts',
]

function Home() {
  const { signIn, signOut } = useAuthActions()
  const {
    data: { viewer },
  } = useSuspenseQuery(convexQuery(api.myFunctions.listNumbers, { count: 10 }))

  const [typingIndex, setTypingIndex] = React.useState(0)
  const [typingText, setTypingText] = React.useState('')
  const [isDeleting, setIsDeleting] = React.useState(false)

  React.useEffect(() => {
    const phrase = HERO_PHRASES[typingIndex]
    const baseDelay = isDeleting ? 50 : 90
    const idleDelay = 900

    const timeout = setTimeout(() => {
      setTypingText((current) => {
        if (!isDeleting) {
          const next = phrase.slice(0, current.length + 1)
          if (next === phrase) {
            setIsDeleting(true)
          }
          return next
        } else {
          const next = phrase.slice(0, current.length - 1)
          if (next.length === 0) {
            setIsDeleting(false)
            setTypingIndex((prev) => (prev + 1) % HERO_PHRASES.length)
          }
          return next
        }
      })
    }, typingText === phrase && !isDeleting ? idleDelay : baseDelay)

    return () => clearTimeout(timeout)
  }, [typingIndex, typingText, isDeleting])

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              Weblens
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {viewer ? (
              <>
                <span className="text-sm text-gray-600">Welcome, {viewer}</span>
                <Link
                  to="/app"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  Go to Dashboard
                </Link>
                <button
                  onClick={() => void signOut()}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Sign out
                </button>
              </>
            ) : (
              <button
                onClick={() => void signIn('google', { redirectTo: '/app' })}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Web evidence studio for
            <span className="block mt-2 text-4xl sm:text-5xl md:text-6xl text-blue-700">
              <span className="border-r-2 border-blue-600 pr-1 animate-pulse">
                {typingText || '\u00A0'}
              </span>
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-3">
            Collaborate in real time, scrape web sources, cite evidence, and publish polished reports
            your team can trust.
          </p>
          <p className="text-sm text-gray-500 max-w-xl mx-auto mb-8">
            Unlike generic docs tools, Weblens is built around web evidence, scraping, and citations
            from day one.
          </p>
          {!viewer && (
            <button
              onClick={() => void signIn('google', { redirectTo: '/app' })}
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-lg hover:shadow-xl transition-all"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Get Started Free
            </button>
          )}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Web Scraping</h3>
            <p className="text-gray-600">
              Automatically extract and store content from any URL using Firecrawl's intelligent scraping.
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Real-time Collaboration</h3>
            <p className="text-gray-600">
              See who's online, add comments, and collaborate with your team in real-time.
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">One-Click Publishing</h3>
            <p className="text-gray-600">
              Publish your research to a public URL with custom slugs and SEO-friendly pages.
            </p>
          </div>
        </div>

        {/* Tech Stack */}
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
          <h3 className="text-center text-sm font-semibold text-gray-500 uppercase tracking-wide mb-6">
            Powered by Modern Tech
          </h3>
          <div className="flex flex-wrap items-center justify-center gap-8">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">TanStack Start</div>
              <div className="text-sm text-gray-600">Full-stack React & routing</div>
            </div>
            <div className="text-gray-300">•</div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">Convex</div>
              <div className="text-sm text-gray-600">Realtime Database</div>
            </div>
            <div className="text-gray-300">•</div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">Firecrawl</div>
              <div className="text-sm text-gray-600">Web Scraping</div>
            </div>
            <div className="text-gray-300">•</div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">Autumn</div>
              <div className="text-sm text-gray-600">Billing & Credits</div>
            </div>
            <div className="text-gray-300">•</div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">Cloudflare</div>
              <div className="text-sm text-gray-600">Edge caching & Workers</div>
            </div>
            <div className="text-gray-300">•</div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">Netlify</div>
              <div className="text-sm text-gray-600">Hosting & Deploys</div>
            </div>
            <div className="text-gray-300">•</div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">Sentry</div>
              <div className="text-sm text-gray-600">Error Monitoring</div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white/80 backdrop-blur-sm mt-24">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center text-sm text-gray-600">
          <p>Built for the TanStack Start Hackathon 2025</p>
        </div>
      </footer>
    </div>
  )
}
